import type { SurfaceKind } from '../world/surface.js';
import type { AudioEngine } from './AudioEngine.js';

/**
 * Surface label used to pick the tire-noise preset. Aliased to `SurfaceKind`
 * from the world surface classifier so the audio system tracks any new
 * categories the world adds automatically — TypeScript will then force us
 * to fill in a corresponding preset below.
 */
export type Surface = SurfaceKind;

/**
 * Speed-driven tire / road-contact noise.
 *
 *   noise → filter (type/center/Q from preset) → speed-driven gain → master
 *
 * Filter type and characteristic frequency come from the active surface preset
 * so road can be a tonal bandpass while offroad is a bass-heavy lowpass on a
 * single shared signal path.
 */
export class TireSound {
  private ctx: AudioContext;
  private noise: AudioBufferSourceNode;
  private filter: BiquadFilterNode;
  private gain: GainNode;
  private volumeGain: GainNode;
  private volume = 1.0;
  private muteGain: GainNode;
  private muted = false;
  private started = false;
  private currentSurface: Surface = 'road';
  /** Tracks the filter type currently in use so we only swap when it changes. */
  private appliedFilterType: BiquadFilterType | null = null;

  constructor(audio: AudioEngine, noiseBuf: AudioBuffer) {
    this.ctx = audio.ctx;

    this.noise = this.ctx.createBufferSource();
    this.noise.buffer = noiseBuf;
    this.noise.loop = true;

    // Filter type, frequency and Q are all driven from the active surface
    // preset, so road can be a tonal bandpass and offroad a bass-heavy lowpass
    // without needing separate signal paths.
    this.filter = this.ctx.createBiquadFilter();

    this.gain = this.ctx.createGain();
    this.gain.gain.value = 0.0;

    // User volume — Settings → Sounds slider, persisted.
    this.volumeGain = this.ctx.createGain();
    this.volumeGain.gain.value = this.volume;

    // User mute — separate from the speed-driven gain so they don't fight.
    this.muteGain = this.ctx.createGain();
    this.muteGain.gain.value = 1.0;

    this.noise
      .connect(this.filter)
      .connect(this.gain)
      .connect(this.volumeGain)
      .connect(this.muteGain)
      .connect(audio.master);
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    this.volumeGain.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.muteGain.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.05);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    // Random offset into the shared noise buffer — decorrelates this source
    // from engine/wind noise to avoid comb filtering across overlapping bands.
    const noiseOffset = Math.random() * (this.noise.buffer?.duration ?? 0);
    this.noise.start(0, noiseOffset);
  }

  /**
   * Call every frame.
   * @param speed   m/s
   * @param surface 'road' | 'offroad' — picks the EQ/volume preset
   */
  update(speed: number, surface: Surface): void {
    if (!this.started) return;
    this.currentSurface = surface;

    const now = this.ctx.currentTime;
    const TAU = 0.1;
    const kmh = speed * 3.6;

    const preset = surfacePresets[surface];

    // Swap filter type/Q only when the active surface changes — switching every
    // frame is wasteful and (on some impls) causes a tiny pop.
    if (this.appliedFilterType !== preset.filterType) {
      this.filter.type = preset.filterType;
      this.filter.Q.value = preset.filterQ;
      this.appliedFilterType = preset.filterType;
    }

    this.gain.gain.setTargetAtTime(preset.gainCurve(kmh), now, TAU);
    this.filter.frequency.setTargetAtTime(preset.centerCurve(kmh), now, TAU);
  }

  getCurrentSurface(): Surface {
    return this.currentSurface;
  }
}

interface SurfacePreset {
  /** Biquad filter type — bandpass for tonal, lowpass for bass-rumble, etc. */
  filterType: BiquadFilterType;
  /** Filter resonance. Higher = more focused / boomy. */
  filterQ: number;
  /** Tire noise gain at the given speed (km/h). */
  gainCurve: (kmh: number) => number;
  /** Filter center / cutoff frequency at the given speed (km/h). */
  centerCurve: (kmh: number) => number;
}

/**
 * Tuning presets per surface — one entry per `SurfaceKind`. Adding a new
 * surface in world/surface.ts is a TypeScript error here until a preset
 * is added, so we can't ship audibly-broken builds where a new surface
 * is silently classified but unheard.
 *
 * Character intent:
 *   road  — smooth tarmac, tonal mid hum, lightest overall
 *   path  — gravel / footway / dirt path: middle ground, more weight than
 *           road but less than grass; broader / less tonal band
 *   grass — full off-road: bass-heavy lowpass thump, no high-frequency
 *           content even at speed
 */
const surfacePresets: Record<Surface, SurfacePreset> = {
  // Smooth tarmac roll — narrow bandpass in the mids for a tonal hum that
  // rises in pitch with speed. Quietest overall.
  road: {
    filterType: 'bandpass',
    filterQ: 0.6,
    gainCurve: (kmh) => {
      if (kmh <= 15) return 0;
      if (kmh <= 120) return 0.05 * ((kmh - 15) / 105);
      return 0.05;
    },
    centerCurve: (kmh) => {
      const t = clamp01((kmh - 15) / 135);
      return 250 + t * 950; // 250 → 1200 Hz
    },
  },
  // Gravel / dirt path / footway — lowpass with a moderate cutoff that opens
  // a bit higher than grass, plus a lower Q so it sounds less "boomy" and
  // more "scuffed". Onset earlier than road (you feel it sooner), peak gain
  // sits between road and grass.
  path: {
    filterType: 'lowpass',
    filterQ: 0.7,
    gainCurve: (kmh) => {
      if (kmh <= 8) return 0;
      if (kmh <= 90) return 0.10 * ((kmh - 8) / 82);
      return 0.10;
    },
    centerCurve: (kmh) => {
      const t = clamp01((kmh - 8) / 92);
      return 150 + t * 550; // 150 → 700 Hz
    },
  },
  // Grass / open terrain — broadband lowpass with a focused resonance for
  // a bass-heavy thump. Starts almost immediately, peaks earlier than road,
  // and never gets bright — even at top speed the cutoff stays under ~500 Hz.
  grass: {
    filterType: 'lowpass',
    filterQ: 0.9,
    gainCurve: (kmh) => {
      if (kmh <= 3) return 0;
      if (kmh <= 70) return 0.14 * ((kmh - 3) / 67);
      return 0.14;
    },
    centerCurve: (kmh) => {
      const t = clamp01((kmh - 3) / 97);
      return 90 + t * 380; // 90 → 470 Hz
    },
  },
};

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
